// Copyright (c) Said Borna. All rights reserved.
/* eslint-disable react-refresh/only-export-components */
import { Navigate, createBrowserRouter, useLocation, type RouteObject } from 'react-router-dom';
import { lazy, Suspense, type ReactElement } from 'react';
import { ProtectedRoute } from '@/components/auth/ProtectedRoute';
import { AppLayout, AdminLayout } from '@/components/layout/AppLayout';
import { ErrorBoundary } from '@/components/ErrorBoundary';

// -- Eagerly loaded (critical path) --
import LoginPage from '@/pages/LoginPage';

// -- Lazy loaded pages --
const DashboardPage = lazy(() => import('@/pages/user/DashboardPage'));
const ContentPage = lazy(() => import('@/pages/user/ContentPage'));
const ContentManagerPage = lazy(() => import('@/pages/admin/ContentManagerPage'));
const CreatePostPage = lazy(() => import('@/pages/admin/CreatePostPage'));
const TeamsPage = lazy(() => import('@/pages/admin/TeamsPage'));
const TeamDetailPage = lazy(() => import('@/pages/admin/TeamDetailPage'));
const UserManagerPage = lazy(() => import('@/pages/admin/UserManagerPage'));
const PipelineManagerPage = lazy(() => import('@/pages/admin/PipelineManagerPage'));
const AggregatedPipelinePage = lazy(() => import('@/pages/admin/AggregatedPipelinePage'));
const AdminCalendarPage = lazy(() => import('@/pages/admin/AdminCalendarPage'));
const AIStudioPage = lazy(() => import('@/pages/admin/AIStudioPage'));
const SuperPromptPage = lazy(() => import('@/pages/admin/SuperPromptPage'));
const CalendarPage = lazy(() => import('@/pages/user/CalendarPage'));
const AcceptInvitePage = lazy(() => import('@/pages/AcceptInvitePage'));
const LinkedInCallbackPage = lazy(() => import('@/pages/LinkedInCallbackPage'));
const WriteBrainPage = lazy(() => import('@/pages/admin/WriteBrainPage'));
const InspirationPage = lazy(() => import('@/pages/admin/InspirationPage'));
const ImportHistoryPage = lazy(() => import('@/pages/admin/ImportHistoryPage'));
const OpsDashboardPage = lazy(() => import('@/pages/admin/OpsDashboardPage'));
const MediaManagementPage = lazy(() => import('@/pages/admin/MediaManagementPage'));
const AdminContentIdeasPage = lazy(() => import('@/pages/admin/AdminContentIdeasPage'));
const ContentIntelligencePage = lazy(() => import('@/pages/admin/ContentIntelligencePage'));
const IntegrationsPage = lazy(() => import('@/pages/admin/IntegrationsPage'));
const PostDiffPage = lazy(() => import('@/pages/admin/PostDiffPage'));
const SuggestionsPage = lazy(() => import('@/pages/admin/SuggestionsPage'));
const PromptConfigPage = lazy(() => import('@/pages/admin/PromptConfigPage'));
const ContentIdeasPage = lazy(() => import('@/pages/user/ContentIdeasPage'));
const MediaLibraryPage = lazy(() => import('@/pages/user/MediaLibraryPage'));
const BrandProfilePage = lazy(() => import('@/pages/user/BrandProfilePage'));
const SettingsPage = lazy(() => import('@/pages/user/SettingsPage'));
const ChangePasswordPage = lazy(() => import('@/pages/user/ChangePasswordPage'));

/**
 * Suspense wrapper for lazy-loaded pages.
 * Shows skeleton shimmer during lazy load.
 */
function PageSuspense({ children }: { readonly children: ReactElement }): ReactElement {
    return (
        <Suspense
            fallback={
                <div className="space-y-6 p-8">
                    <div className="skeleton h-8 w-48 rounded-lg" />
                    <div className="skeleton h-4 w-72 rounded-lg" />
                    <div className="grid gap-4 md:grid-cols-3">
                        <div className="skeleton h-32 rounded-xl" />
                        <div className="skeleton h-32 rounded-xl" />
                        <div className="skeleton h-32 rounded-xl" />
                    </div>
                    <div className="skeleton h-64 rounded-xl" />
                </div>
            }
        >
            {children}
        </Suspense>
    );
}

/**
 * Client route wrapper — applies AppLayout, auth guard, and error boundary.
 */
function ClientRoute({ children, context }: { readonly children: ReactElement; readonly context?: string }): ReactElement {
    const location = useLocation();

    return (
        <ProtectedRoute>
            <AppLayout>
                <ErrorBoundary key={location.pathname} context={context ?? 'Client'}>
                    <PageSuspense>{children}</PageSuspense>
                </ErrorBoundary>
            </AppLayout>
        </ProtectedRoute>
    );
}

/**
 * Staff route wrapper — applies AdminLayout, staff-only guard, and error boundary.
 */
function StaffRoute({ children, context }: { readonly children: ReactElement; readonly context?: string }): ReactElement {
    const location = useLocation();

    return (
        <ProtectedRoute staffOnly>
            <AdminLayout>
                <ErrorBoundary key={location.pathname} context={context ?? 'Admin'}>
                    <PageSuspense>{children}</PageSuspense>
                </ErrorBoundary>
            </AdminLayout>
        </ProtectedRoute>
    );
}

const routes: RouteObject[] = [
    // -- Public routes --
    {
        path: '/',
        element: <LoginPage />,
    },
    {
        path: '/accept-invite',
        element: (
            <ErrorBoundary context="Accept Invite">
                <PageSuspense>
                    <AcceptInvitePage />
                </PageSuspense>
            </ErrorBoundary>
        ),
    },

    // -- Client routes --
    {
        path: '/dashboard',
        element: (
            <ClientRoute context="Dashboard">
                <DashboardPage />
            </ClientRoute>
        ),
    },
    {
        path: '/content',
        element: (
            <ClientRoute context="Content">
                <ContentPage />
            </ClientRoute>
        ),
    },
    {
        path: '/calendar',
        element: (
            <ClientRoute>
                <CalendarPage />
            </ClientRoute>
        ),
    },
    {
        path: '/content-ideas',
        element: (
            <ClientRoute>
                <ContentIdeasPage />
            </ClientRoute>
        ),
    },
    {
        path: '/media-library',
        element: (
            <ClientRoute>
                <MediaLibraryPage />
            </ClientRoute>
        ),
    },
    {
        path: '/profile',
        element: (
            <ClientRoute context="Brand Profile">
                <BrandProfilePage />
            </ClientRoute>
        ),
    },
    {
        path: '/settings',
        element: (
            <ClientRoute context="Settings">
                <SettingsPage />
            </ClientRoute>
        ),
    },
    {
        path: '/settings/change-password',
        element: (
            <ClientRoute context="Change Password">
                <ChangePasswordPage />
            </ClientRoute>
        ),
    },
    {
        path: '/linkedin-callback',
        element: (
            <ErrorBoundary context="LinkedIn Callback">
                <PageSuspense>
                    <LinkedInCallbackPage />
                </PageSuspense>
            </ErrorBoundary>
        ),
    },

    // -- Staff routes --
    {
        path: '/admin/content-manager',
        element: (
            <StaffRoute context="Content Manager">
                <ContentManagerPage />
            </StaffRoute>
        ),
    },
    {
        path: '/admin/create-post',
        element: (
            <StaffRoute>
                <CreatePostPage />
            </StaffRoute>
        ),
    },
    {
        path: '/admin/teams',
        element: (
            <StaffRoute>
                <TeamsPage />
            </StaffRoute>
        ),
    },
    {
        path: '/admin/teams/:teamId',
        element: (
            <StaffRoute>
                <TeamDetailPage />
            </StaffRoute>
        ),
    },
    {
        path: '/admin/user-manager',
        element: (
            <StaffRoute>
                <UserManagerPage />
            </StaffRoute>
        ),
    },
    {
        path: '/admin/pipeline-manager',
        element: (
            <StaffRoute context="Pipeline">
                <PipelineManagerPage />
            </StaffRoute>
        ),
    },
    {
        path: '/admin/aggregated-pipeline',
        element: (
            <StaffRoute context="Aggregated Pipeline">
                <AggregatedPipelinePage />
            </StaffRoute>
        ),
    },
    {
        path: '/admin/calendar',
        element: (
            <StaffRoute>
                <AdminCalendarPage />
            </StaffRoute>
        ),
    },
    {
        path: '/admin/ai-studio',
        element: (
            <StaffRoute context="AI Studio">
                <AIStudioPage />
            </StaffRoute>
        ),
    },
    {
        path: '/admin/brand-profile',
        element: (
            <StaffRoute>
                <SuperPromptPage />
            </StaffRoute>
        ),
    },
    {
        path: '/admin/super-prompts',
        element: <Navigate to="/admin/brand-profile" replace />,
    },
    {
        path: '/admin/write-brain',
        element: (
            <StaffRoute>
                <WriteBrainPage />
            </StaffRoute>
        ),
    },
    {
        path: '/admin/inspiration',
        element: (
            <StaffRoute>
                <InspirationPage />
            </StaffRoute>
        ),
    },
    {
        path: '/admin/import-history',
        element: (
            <StaffRoute>
                <ImportHistoryPage />
            </StaffRoute>
        ),
    },
    {
        path: '/admin/writer-performance',
        element: (
            <StaffRoute context="Writer Performance">
                <OpsDashboardPage />
            </StaffRoute>
        ),
    },
    {
        path: '/admin/ops-dashboard',
        element: <Navigate to="/admin/writer-performance" replace />,
    },
    {
        path: '/admin/media-management',
        element: (
            <StaffRoute>
                <MediaManagementPage />
            </StaffRoute>
        ),
    },
    {
        path: '/admin/content-ideas',
        element: (
            <StaffRoute context="Content Ideas">
                <AdminContentIdeasPage />
            </StaffRoute>
        ),
    },
    {
        path: '/admin/content-intelligence',
        element: (
            <StaffRoute context="Content Intelligence">
                <ContentIntelligencePage />
            </StaffRoute>
        ),
    },
    {
        path: '/admin/integrations',
        element: (
            <StaffRoute context="Integrations">
                <IntegrationsPage />
            </StaffRoute>
        ),
    },
    {
        path: '/admin/tone-corpus',
        element: (
            <Navigate to="/admin/brand-profile" replace />
        ),
    },
    {
        path: '/admin/post-diff',
        element: (
            <StaffRoute context="Post Diff">
                <PostDiffPage />
            </StaffRoute>
        ),
    },
    {
        path: '/admin/suggestions',
        element: (
            <StaffRoute context="Best Practice Queue">
                <SuggestionsPage />
            </StaffRoute>
        ),
    },
    {
        path: '/admin/edit-insights',
        element: <Navigate to="/admin/writer-performance" replace />,
    },
    {
        path: '/admin/prompt-config',
        element: (
            <StaffRoute context="Prompt Configuration">
                <PromptConfigPage />
            </StaffRoute>
        ),
    },

    // -- 404 catch-all --
    {
        path: '*',
        element: (
            <div className="flex min-h-screen items-center justify-center bg-[#FAFAFA]">
                <div className="text-center">
                    <h1 className="mb-2 text-4xl font-bold text-foreground">404</h1>
                    <p className="mb-6 text-muted-foreground">Page not found</p>
                    <a
                        href="/"
                        className="inline-flex h-10 items-center justify-center rounded-lg bg-foreground px-6 text-sm font-medium text-white transition-colors hover:bg-foreground/90"
                    >
                        Go home
                    </a>
                </div>
            </div>
        ),
    },
];

/**
 * Application router.
 * All routes defined here — do not add routes elsewhere.
 */
export const router = createBrowserRouter(routes);
